<template>
  <div class="billing-history">
    <div v-if="loading" class="invoice-loading">
      <v-skeleton-loader v-for="index in 3" :key="index" type="list-item-two-line" class="invoice-skeleton" />
    </div>

    <div v-else-if="previewInvoices.length" class="invoice-preview">
      <button
        v-for="invoice in previewInvoices"
        :key="invoice.id"
        type="button"
        class="invoice-preview-row"
        @click="viewInvoice(invoice)"
      >
        <div class="invoice-icon"><v-icon color="primary">mdi-receipt-text-outline</v-icon></div>
        <div class="invoice-primary">
          <div class="invoice-number">{{ invoice.number || "Stripe invoice" }}</div>
          <div class="invoice-date">{{ invoice.created | moment("MMMM D, YYYY") }}</div>
        </div>
        <v-chip x-small :class="['invoice-status', 'invoice-status-' + invoice.status]">{{ invoice.status }}</v-chip>
        <div class="invoice-amount">{{ formatAmount(invoice) }}</div>
        <v-icon small color="grey">mdi-open-in-new</v-icon>
      </button>

      <div v-if="invoices.length > 3" class="view-all-wrap">
        <v-btn text rounded small color="primary" @click="dialog = true">
          View all billing history
          <v-icon right small>mdi-arrow-right</v-icon>
        </v-btn>
      </div>
    </div>

    <div v-else class="invoice-empty">
      <div class="empty-icon"><v-icon color="blue-grey lighten-2" size="34">mdi-receipt-text-outline</v-icon></div>
      <div class="empty-title">No billing history yet</div>
      <div class="empty-subtitle">Your Stripe invoices will appear here after your first bill.</div>
    </div>

    <v-dialog v-model="dialog" max-width="980" scrollable>
      <v-card class="invoice-dialog">
        <div class="dialog-header">
          <div class="dialog-title-wrap">
            <div class="dialog-icon"><v-icon color="primary">mdi-receipt-text-clock-outline</v-icon></div>
            <div>
              <div class="dialog-title">Billing History</div>
              <div class="dialog-subtitle">Review and open invoices securely through Stripe.</div>
            </div>
          </div>
          <v-btn icon small @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>

        <v-card-text class="dialog-body">
          <div class="history-summary">
            <div><span>Invoices</span><strong>{{ invoices.length }}</strong></div>
            <div><span>Latest invoice</span><strong>{{ latestInvoiceDate }}</strong></div>
          </div>

          <div class="invoice-table-wrap">
            <v-data-table
              :mobile-breakpoint="0"
              :headers="headers"
              :items="sortedInvoices"
              :loading="loading"
              sort-by="created"
              :sort-desc="true"
              :page.sync="page"
              :items-per-page="itemsPerPage"
              :footer-props="{ 'items-per-page-options': [5, 10, 25] }"
              class="invoice-table"
            >
              <template v-slot:item="{ item }">
                <tr class="invoice-row">
                  <td>
                    <div class="table-date">{{ item.created | moment("MM/DD/YYYY") }}</div>
                    <div class="table-subtitle">{{ item.created | moment("LT") }}</div>
                  </td>
                  <td>
                    <div class="table-number">{{ item.number || "Pending" }}</div>
                    <div class="table-subtitle">{{ abbreviatedReference(item.charge || item.id) }}</div>
                  </td>
                  <td class="text-right table-amount">{{ formatAmount(item) }}</td>
                  <td>
                    <v-chip x-small :class="['invoice-status', 'invoice-status-' + item.status]">{{ item.status }}</v-chip>
                  </td>
                  <td class="text-right">
                    <v-btn text rounded small color="primary" :disabled="!item.hosted_invoice_url" @click="viewInvoice(item)">
                      View invoice<v-icon right small>mdi-open-in-new</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </template>
              <template v-slot:no-data>
                <div class="invoice-empty compact"><div class="empty-title">No invoices found</div></div>
              </template>
            </v-data-table>
          </div>
        </v-card-text>

        <v-card-actions class="dialog-actions">
          <v-spacer />
          <v-btn text rounded color="grey darken-1" @click="dialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import accountService from "../service";
import { mapGetters } from "vuex";

export default {
  name: "billing-invoice-list",
  data() {
    return {
      dialog: false,
      headers: [
        { text: "Date", value: "created", sortable: true },
        { text: "Invoice", value: "number", sortable: true },
        { text: "Amount", value: "amount_due", align: "right", sortable: true },
        { text: "Status", value: "status", sortable: true },
        { text: "", value: "action", align: "right", sortable: false },
      ],
      invoices: [],
      loading: true,
      page: 1,
      itemsPerPage: 10,
    };
  },
  computed: {
    ...mapGetters("global", ["currentBusiness"]),
    sortedInvoices() {
      return [...this.invoices].sort((a, b) => Number(b.created || 0) - Number(a.created || 0));
    },
    previewInvoices() {
      return this.sortedInvoices.slice(0, 3);
    },
    latestInvoiceDate() {
      return this.sortedInvoices.length
        ? this.$moment.unix(this.sortedInvoices[0].created).format("MMMM D, YYYY")
        : "Not available";
    },
  },
  created() {
    this.getList();
  },
  methods: {
    viewInvoice(invoice) {
      if (!invoice.hosted_invoice_url) return;
      window.open(invoice.hosted_invoice_url, "_blank", "noopener,noreferrer");
    },
    formatAmount(invoice) {
      const currency = (invoice.currency || "usd").toUpperCase();
      const amount = invoice.amount_due !== undefined ? invoice.amount_due : invoice.amount_paid;
      return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(Number(amount || 0) / 100);
    },
    abbreviatedReference(value) {
      if (!value) return "No payment reference";
      const reference = String(value);
      return reference.length > 20 ? `${reference.slice(0, 10)}…${reference.slice(-6)}` : reference;
    },
    async getList() {
      if (!this.currentBusiness || !this.currentBusiness.customer_id) {
        this.invoices = [];
        this.loading = false;
        return;
      }
      this.loading = true;
      try {
        const response = await accountService.getAllInvoice({ customer_id: this.currentBusiness.customer_id });
        this.invoices = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        console.error("Unable to load billing history", error);
        this.invoices = [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.billing-history{width:100%}.invoice-loading,.invoice-preview{display:flex;flex-direction:column;gap:8px}.invoice-skeleton{border:1px solid #e2e8f0;border-radius:14px}.invoice-preview-row{width:100%;padding:12px 14px;border:1px solid #e2e8f0;border-radius:14px;background:#fff;display:grid;grid-template-columns:auto minmax(0,1fr) auto auto auto;align-items:center;gap:12px;text-align:left;transition:.15s ease}.invoice-preview-row:hover{background:#f8fafc;border-color:#bfdbfe;transform:translateY(-1px)}.invoice-icon,.dialog-icon,.empty-icon{display:flex;align-items:center;justify-content:center;background:#eef4ff}.invoice-icon{width:40px;height:40px;border-radius:12px}.invoice-number,.table-number{font-size:13px;font-weight:800;color:#334155}.invoice-date,.table-subtitle,.dialog-subtitle,.empty-subtitle{font-size:11px;color:#64748b}.invoice-amount,.table-amount{font-size:14px;font-weight:800;color:#0f172a}.invoice-status{text-transform:capitalize;font-weight:800}.invoice-status-paid{background:#ecfdf3!important;color:#15803d!important}.invoice-status-open,.invoice-status-draft{background:#eff6ff!important;color:#1d4ed8!important}.invoice-status-void,.invoice-status-uncollectible{background:#fff1f2!important;color:#be123c!important}.view-all-wrap{text-align:center;padding-top:5px}.invoice-empty{text-align:center;padding:34px 16px}.invoice-empty.compact{padding:28px}.empty-icon{width:60px;height:60px;border-radius:18px;margin:0 auto 10px}.empty-title{font-size:15px;font-weight:800;color:#334155;margin-bottom:3px}.invoice-dialog{border-radius:22px!important;overflow:hidden}.dialog-header{padding:18px 20px;border-bottom:1px solid #e2e8f0;display:flex;align-items:center;justify-content:space-between}.dialog-title-wrap{display:flex;align-items:center}.dialog-icon{width:42px;height:42px;border-radius:13px;margin-right:12px}.dialog-title{font-size:20px;font-weight:800;color:#0f172a}.dialog-body{padding:20px!important;background:#f8fafc}.history-summary{display:flex;gap:12px;margin-bottom:14px}.history-summary>div{min-width:160px;padding:12px 14px;background:#fff;border-radius:14px;border:1px solid #e2e8f0;display:flex;flex-direction:column}.history-summary span{font-size:10px;color:#64748b}.history-summary strong{font-size:15px;color:#0f172a;margin-top:2px}.invoice-table-wrap{border:1px solid #e2e8f0;border-radius:16px;overflow:hidden;background:#fff}.invoice-row:hover{background:#f8fafc}.table-date{font-size:13px;font-weight:700;color:#334155}.dialog-actions{padding:12px 20px!important;border-top:1px solid #e2e8f0}@media(max-width:650px){.invoice-preview-row{grid-template-columns:auto minmax(0,1fr) auto}.invoice-status{display:none}.invoice-amount{grid-column:2}.invoice-preview-row>.v-icon{grid-column:3;grid-row:1/3}.history-summary{flex-direction:column}.history-summary>div{min-width:0}.dialog-body{padding:12px!important}}
</style>
